import React, { useCallback, useState } from 'react';
import { DisplayType } from 'App/babel/attributes/Attribute';
import type Contact from 'App/resources/Model/Contact';
import { ListViewDefaultColumn } from 'App/components/ListView/ListViewRenderHelpers';
import { TableColumn, TableRow } from '@upsales/components';
import { ListViewTableProvided } from 'App/components/ListView/ListViewTable/ListViewTable';
import ActionColumn, { Action } from '../../CompanyGroupCard/ActionColumn';
import ListView from 'App/components/ListView';
import ContactAttributes from 'App/babel/attributes/Contact';
import contactFilters from 'App/babel/filterConfigs/Contact';
const ContactFilters = contactFilters();
import ContactResource from 'App/resources/Contact';
import { useTranslation } from 'Components/Helpers/translate';
import RequestBuilder, { comparisonTypes as ComparisonTypes } from 'Resources/RequestBuilder';
import BemClass from '@upsales/components/Utils/bemClass';
import { useFeatureAvailable } from 'App/components/hooks';
import { Feature } from 'Store/actions/FeatureHelperActions';
import RelationsSignalWidget from 'App/components/RelationsSignalWidget/RelationsSignalWidget';
import ContactPriorityIcon from 'App/components/columns/ContactPriorityIcon';
import type { ContactFilter } from '../ContactsTab';
import _ from 'lodash';

import './ContactList.scss';

type Props = {
	columns?: string[];
	clientIds: number[];
	hideHeader?: boolean;
	filter?: ContactFilter;
	getDataFilter: (rb: RequestBuilder, clientIds: number[]) => void;
	clientId?: number;
};

const ContactList = (props: Props) => {
	const {
		clientIds,
		hideHeader,
		filter = 'all',
		getDataFilter,
		clientId,
		columns = ['priority', 'name', 'title', 'operationalAccount', 'history', 'email', 'phone', 'cellPhone', '']
	} = props;

	const { t } = useTranslation();
	const classes = new BemClass('ContactList');
	const [initialSorting, setInitialSorting] = useState([{ attribute: 'name', ascending: true }]);
	const hasAI = useFeatureAvailable(Feature.AI);

	const renderTableRow = (contact: Contact, { columns, attributes }: ListViewTableProvided<Contact>) => {
		const goTo = (contact: Contact) => {
			Tools.$state.go('contact.dashboard', { id: contact.id });
		};

		const columnElements = columns.map(column => {
			let content = null;
			switch (column) {
				case 'priority': {
					content = <ContactPriorityIcon contact={contact} className={classes.elem('priority-icon').b()} />;
					break;
				}
				case '': {
					content = <ActionColumn object={contact} entity="contact" actions={[Action.Edit, Action.Delete]} />;
					break;
				}
				case 'signals': {
					content = contact.client?.id ? (
						<RelationsSignalWidget
							barOnly
							clientId={contact.client.id}
							contactId={contact.id}
							signals={contact.signals}
						/>
					) : null;
					break;
				}
				default: {
					return (
						<ListViewDefaultColumn<Contact & { accountId?: number }>
							key={column}
							item={{ ...contact, accountId: clientId }}
							attributes={attributes}
							column={column}
						/>
					);
				}
			}
			return <TableColumn key={column + contact.id}>{content}</TableColumn>;
		});

		return (
			<TableRow inactive={!contact.active} key={contact.id} onClick={() => goTo(contact)}>
				{columnElements}
			</TableRow>
		);
	};

	const getData = useCallback(
		async (rb: RequestBuilder) => {
			if (clientIds.length === 0) {
				return {
					data: [],
					metadata: { total: 0, limit: 50, offset: 0 }
				};
			}

			switch (filter) {
				case 'starred':
					rb.addFilter({ field: 'active' }, ComparisonTypes.Equals, true);
					rb.addFilter(ContactAttributes.priority, ComparisonTypes.Equals, true);
					break;
				case 'all':
					rb.addFilter({ field: 'active' }, ComparisonTypes.Equals, true);
					break;
				case 'inactive':
					rb.addFilter({ field: 'active' }, ComparisonTypes.Equals, false);
					break;
			}

			getDataFilter(rb, clientIds);

			const sorts = rb.build().sort || [];
			// Insert isPriority sorting at the beginning
			sorts.unshift({ a: 'isPriority', s: 'Z' });

			return ContactResource.find({ ...rb.build(), includeSignals: hasAI, sort: sorts });
		},
		[filter, clientIds]
	);

	const modifiedContactAttributes = _.cloneDeep(ContactAttributes);
	modifiedContactAttributes.name.displayType = DisplayType.ContactTitle;
	modifiedContactAttributes.title.displayType = DisplayType.EllipsisText;
	modifiedContactAttributes.priority.sortable = false;

	return (
		<ListView<Contact>
			key={`${filter}-${initialSorting.map(sort => `${sort.ascending}`).join('-')}`}
			filterConfigs={ContactFilters}
			hideFilters
			className={classes.b()}
			broadcastType="contact"
			getData={getData}
			attributes={modifiedContactAttributes}
			renderTableRow={renderTableRow}
			columns={columns}
			skipSortById
			renderHeader={hideHeader ? () => null : undefined}
			quickSearchFilter={!hideHeader ? 'ListSearch' : undefined}
			quickSearchPlaceholder={t('agreement.moveSubscriptionContactSelectPlaceholder')}
			renderToolsColumn={false}
			canSortCustomFields={false}
			initialSorting={initialSorting}
			onSortChange={({ field }) => {
				if (field === 'client.operationalAccount.name') {
					setInitialSorting(prev =>
						prev.map(sort => ({
							...sort,
							ascending: !sort.ascending,
							missing: sort.ascending ? 'first' : 'last'
						}))
					);
				}
			}}
		/>
	);
};

export default ContactList;
